/**
 * Dashboard — UX/UI Specification §9.3.
 *
 * Exactly three things. No charts, no activity feed, no analytics: the studio
 * opens this to start work, and everything else on the page is in the way.
 */

import { Link } from 'react-router-dom';
import { useApiData } from '../hooks/useApiData.js';
import type { DashboardResponse } from '../api/types.js';
import { useSession } from '../session/SessionContext.js';
import './Dashboard.css';

interface InstructorSummary {
  id: string;
  status: 'active' | 'inactive';
}

export function Dashboard() {
  const { session } = useSession();
  const { data, error, loading } = useApiData<DashboardResponse>('/api/dashboard');
  const instructors = useApiData<{ items: InstructorSummary[] }>('/api/instructors');

  const owner = session?.role === 'owner';
  const noInstructors =
    !instructors.loading && (instructors.data?.items.length ?? 0) === 0;
  const noStudents = !loading && (data?.activeStudents ?? 0) === 0;

  return (
    <section className="dash">
      <h1>Panel</h1>

      {error && (
        <p className="dash__error" role="alert">
          {error}
        </p>
      )}

      {/*
       * A studio that has just been handed its password opens this screen. Three
       * zeroes and a report button it cannot use would be a worse answer than
       * naming the first step, which is always the same one: add an instructor,
       * because a student has to belong to one.
       */}
      {owner && noInstructors && (
        <div className="dash__start">
          <h2>Hoş geldiniz</h2>
          <p>
            Salonunuz henüz boş. İlk adım bir <b>eğitmen eklemek</b> — her öğrenci bir
            eğitmene ait olur, ve her eğitmen kendi şifresiyle girip yalnızca kendi
            öğrencilerini görür.
          </p>
          <Link to="/egitmenler" className="dash__start-cta">
            Eğitmen Ekle
          </Link>
        </div>
      )}

      {owner && !noInstructors && noStudents && (
        <div className="dash__start">
          <h2>İlk öğrencinizi ekleyin</h2>
          <p>
            Eğitmenleriniz hazır. Şimdi bir öğrenci ekleyip ölçümlerini girebilirsiniz.
          </p>
          <Link to="/ogrenciler/yeni" className="dash__start-cta">
            Öğrenci Ekle
          </Link>
        </div>
      )}

      <div className="dash__cards">
        <Link to="/ogrenciler" className="dash__card">
          <span className="dash__card-label">Aktif Öğrenci</span>
          <span className="dash__card-number">
            {loading ? '—' : (data?.activeStudents ?? 0)}
          </span>
        </Link>

        <Link to="/raporlar" className="dash__card">
          <span className="dash__card-label">Bu Ayki Rapor</span>
          <span className="dash__card-number">
            {loading ? '—' : (data?.reportsThisMonth ?? 0)}
          </span>
        </Link>

        <Link to="/rapor-olustur" className="dash__card dash__card--cta">
          <span className="dash__card-label">Rapor Oluştur</span>
          <span className="dash__card-arrow" aria-hidden="true">
            →
          </span>
        </Link>
      </div>
    </section>
  );
}
